// Real audio analysis using Web Audio API + Essentia.js
// NO MOCK DATA - actual BPM, key, frequency, energy, danceability, and vibe analysis

import Essentia from 'essentia.js/dist/essentia.js-core.es.js'
import { EssentiaWASM } from 'essentia.js/dist/essentia-wasm.es.js' // ES6 module (synchronous)

class AudioAnalysisService {
  constructor() {
    this.analysisCache = new Map()
    this.audioContext = null
    this.essentia = null
    this.essentiaReady = false
  }

  // Initialize Essentia.js (WASM-based music analysis library)
  initEssentia() {
    if (this.essentiaReady) return this.essentia

    try {
      console.log('Initializing Essentia.js WASM module...')
      this.essentia = new Essentia(EssentiaWASM)
      this.essentiaReady = true
      console.log('✓ Essentia.js ready!')
      return this.essentia
    } catch (error) {
      console.error('Failed to initialize Essentia.js:', error)
      this.essentiaReady = false
      return null
    }
  }

  getAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    }
    return this.audioContext
  }

  // Fetch and decode audio from Railway backend
  async loadAudio(downloadUrl) {
    try {
      console.log('Downloading MP3 audio from Railway backend:', downloadUrl)

      const response = await fetch(downloadUrl)
      if (!response.ok) {
        throw new Error(`Failed to download audio: ${response.status}`)
      }

      // Check content type - Railway might be returning JSON error instead of audio
      const contentType = response.headers.get('content-type')
      console.log('Response Content-Type:', contentType)

      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json()
        console.error('Railway returned error:', errorData)
        throw new Error(`Railway error: ${errorData.message || errorData.error}`)
      }

      if (contentType && !contentType.includes('audio')) {
        console.error('Unexpected content type:', contentType)
        const text = await response.text()
        console.error('Response body:', text.substring(0, 500))
        throw new Error(`Expected audio, got ${contentType}`)
      }

      console.log('Decoding MP3 audio buffer...')
      const arrayBuffer = await response.arrayBuffer()
      const audioContext = this.getAudioContext()

      const fullAudioBuffer = await audioContext.decodeAudioData(arrayBuffer)
      console.log('Audio decoded successfully:', fullAudioBuffer.duration, 'seconds')

      // PERFORMANCE: Analyze multiple 10s sections and combine
      // DnB has sparse beats + breakdowns - need multiple samples for accuracy
      const sectionDuration = 10 // 10 seconds per section
      const sections = [
        30, // 30-40s
        60, // 60-70s
        90  // 90-100s
      ]

      if (fullAudioBuffer.duration > 100) {
        const sampleRate = fullAudioBuffer.sampleRate
        const numberOfChannels = fullAudioBuffer.numberOfChannels
        const sectionLength = sampleRate * sectionDuration
        const totalLength = sectionLength * sections.length

        // Combine multiple sections for better beat detection
        const combinedBuffer = audioContext.createBuffer(numberOfChannels, totalLength, sampleRate)

        for (let channel = 0; channel < numberOfChannels; channel++) {
          const fullData = fullAudioBuffer.getChannelData(channel)
          const combinedData = combinedBuffer.getChannelData(channel)

          let writeOffset = 0
          for (const startOffset of sections) {
            const startSample = Math.floor(sampleRate * startOffset)
            const section = fullData.subarray(startSample, startSample + sectionLength)
            combinedData.set(section, writeOffset)
            writeOffset += sectionLength
          }
        }

        console.log(`Using ${sections.length} sections (30s total) at 30s, 60s, 90s (full track: ${fullAudioBuffer.duration.toFixed(1)}s)`)
        return combinedBuffer
      }

      return fullAudioBuffer
    } catch (error) {
      console.error('Error loading audio:', error)
      throw error
    }
  }

  // Professional BPM detection using Essentia.js RhythmExtractor2013
  // Research: https://essentia.upf.edu/reference/std_RhythmExtractor2013.html
  // CRITICAL: Requires 44100 Hz sample rate!
  async detectBPM(audioBuffer) {
    const essentia = this.initEssentia()

    if (!essentia || !this.essentiaReady) {
      throw new Error('Essentia.js not initialized')
    }

    try {
      // CRITICAL: RhythmExtractor2013 requires 44100 Hz sample rate
      // Resample audio if needed
      let processBuffer = audioBuffer
      if (audioBuffer.sampleRate !== 44100) {
        console.log(`Resampling from ${audioBuffer.sampleRate} Hz to 44100 Hz for Essentia`)
        processBuffer = await this.resampleTo44100(audioBuffer)
      }

      // Convert to mono signal for Essentia
      const signal = processBuffer.getChannelData(0)
      const signalVector = essentia.arrayToVector(signal)

      // RhythmExtractor2013: Professional beat tracking algorithm
      // Returns: bpm, beats, confidence, estimates, bpmIntervals
      const rhythm = essentia.RhythmExtractor2013(signalVector)

      const bpm = rhythm.bpm
      console.log('Essentia RhythmExtractor2013 BPM:', bpm)
      console.log('Confidence:', rhythm.confidence)

      return Math.round(bpm)
    } catch (error) {
      console.error('Essentia BPM detection failed:', error)
      throw error
    }
  }

  // Resample audio to 44100 Hz (required by Essentia)
  async resampleTo44100(audioBuffer) {
    const offlineContext = new OfflineAudioContext(
      1, // mono
      Math.floor(audioBuffer.duration * 44100), // new length at 44100 Hz
      44100 // target sample rate
    )

    const source = offlineContext.createBufferSource()
    source.buffer = audioBuffer
    source.connect(offlineContext.destination)
    source.start(0)

    return await offlineContext.startRendering()
  }

  // Real key detection using pitch analysis
  async detectKey(audioBuffer) {
    const offlineContext = new OfflineAudioContext(
      1,
      audioBuffer.length,
      audioBuffer.sampleRate
    )

    const source = offlineContext.createBufferSource()
    source.buffer = audioBuffer

    // Focus on mid frequencies where melody typically sits
    const bandpass = offlineContext.createBiquadFilter()
    bandpass.type = 'bandpass'
    bandpass.frequency.value = 440 // A4 reference
    bandpass.Q.value = 1.0

    source.connect(bandpass)
    bandpass.connect(offlineContext.destination)
    source.start(0)

    const renderedBuffer = await offlineContext.startRendering()
    const data = renderedBuffer.getChannelData(0)

    // Perform FFT to detect dominant frequencies
    const fftSize = 8192
    const frequencies = new Float32Array(fftSize)

    // Sample multiple points in the track
    const samplePoints = 10
    const pitchClasses = new Array(12).fill(0) // 12 semitones in an octave

    for (let i = 0; i < samplePoints; i++) {
      const offset = Math.floor((data.length / samplePoints) * i)
      const slice = data.slice(offset, offset + fftSize)

      // Simple autocorrelation for pitch detection
      const pitch = this.autocorrelate(slice, audioBuffer.sampleRate)
      if (pitch > 0) {
        const pitchClass = Math.round(12 * Math.log2(pitch / 440)) % 12
        pitchClasses[(pitchClass + 12) % 12]++
      }
    }

    // Find dominant pitch class
    const dominantPitchClass = pitchClasses.indexOf(Math.max(...pitchClasses))

    // Convert to Camelot notation (8A, 9A, etc.)
    const camelotWheel = [
      '8A', '3A', '10A', '5A', '12A', '7A', '2A', '9A', '4A', '11A', '6A', '1A',
      '5B', '12B', '7B', '2B', '9B', '4B', '11B', '6B', '1B', '8B', '3B', '10B'
    ]

    const key = camelotWheel[dominantPitchClass % 24]
    console.log('Detected key:', key)
    return key
  }

  // Autocorrelation for pitch detection
  autocorrelate(buffer, sampleRate) {
    const SIZE = buffer.length
    const MAX_SAMPLES = Math.floor(SIZE / 2)
    let best_offset = -1
    let best_correlation = 0
    let rms = 0

    // Calculate RMS
    for (let i = 0; i < SIZE; i++) {
      rms += buffer[i] * buffer[i]
    }
    rms = Math.sqrt(rms / SIZE)

    if (rms < 0.01) return -1 // Signal too quiet

    // Find the best offset
    let lastCorrelation = 1
    for (let offset = 0; offset < MAX_SAMPLES; offset++) {
      let correlation = 0

      for (let i = 0; i < MAX_SAMPLES; i++) {
        correlation += Math.abs((buffer[i]) - (buffer[i + offset]))
      }

      correlation = 1 - (correlation / MAX_SAMPLES)

      if (correlation > 0.9 && correlation > lastCorrelation) {
        const foundGoodCorrelation = correlation > best_correlation
        if (foundGoodCorrelation) {
          best_correlation = correlation
          best_offset = offset
        }
      }

      lastCorrelation = correlation
    }

    if (best_offset === -1) return -1

    return sampleRate / best_offset
  }

  // Real frequency spectrum analysis (sub-bass, bass, mids, highs)
  async analyzeFrequencies(audioBuffer) {
    // Create audio context and play audio to analyser
    const audioContext = this.getAudioContext()
    const source = audioContext.createBufferSource()
    source.buffer = audioBuffer

    const analyser = audioContext.createAnalyser()
    analyser.fftSize = 4096
    analyser.smoothingTimeConstant = 0

    source.connect(analyser)
    source.start(0)

    // Wait a bit for audio to start playing
    await new Promise(resolve => setTimeout(resolve, 100))

    // Sample frequency data multiple times throughout playback
    const sampleCount = 20
    const sampleInterval = 200 // ms

    let totalSubBass = 0
    let totalBass = 0
    let totalMid = 0
    let totalHigh = 0
    let validSamples = 0

    for (let i = 0; i < sampleCount; i++) {
      // Get frequency data
      const bufferLength = analyser.frequencyBinCount
      const dataArray = new Uint8Array(bufferLength)
      analyser.getByteFrequencyData(dataArray)

      // Check if we got real data (not all zeros)
      const hasData = dataArray.some(val => val > 0)
      if (!hasData) {
        await new Promise(resolve => setTimeout(resolve, sampleInterval))
        continue
      }

      validSamples++

      // Calculate frequency bands
      const sampleRate = audioBuffer.sampleRate
      const nyquist = sampleRate / 2
      const binWidth = nyquist / bufferLength

      // Sub-bass: 20-60 Hz
      const subBassStart = Math.floor(20 / binWidth)
      const subBassEnd = Math.floor(60 / binWidth)
      totalSubBass += this.calculateBandEnergy(dataArray, subBassStart, subBassEnd)

      // Bass: 60-250 Hz
      const bassStart = Math.floor(60 / binWidth)
      const bassEnd = Math.floor(250 / binWidth)
      totalBass += this.calculateBandEnergy(dataArray, bassStart, bassEnd)

      // Mids: 250-4000 Hz
      const midStart = Math.floor(250 / binWidth)
      const midEnd = Math.floor(4000 / binWidth)
      totalMid += this.calculateBandEnergy(dataArray, midStart, midEnd)

      // Highs: 4000+ Hz
      const highStart = Math.floor(4000 / binWidth)
      const highEnd = bufferLength
      totalHigh += this.calculateBandEnergy(dataArray, highStart, highEnd)

      // Wait before next sample
      await new Promise(resolve => setTimeout(resolve, sampleInterval))
    }

    // Stop playback
    source.stop()

    // Average across valid samples
    const subBassEnergy = validSamples > 0 ? totalSubBass / validSamples : 0
    const bassEnergy = validSamples > 0 ? totalBass / validSamples : 0
    const midEnergy = validSamples > 0 ? totalMid / validSamples : 0
    const highEnergy = validSamples > 0 ? totalHigh / validSamples : 0

    console.log('Frequency analysis (valid samples:', validSamples, '):', {
      subBass: subBassEnergy,
      bass: bassEnergy,
      mid: midEnergy,
      high: highEnergy
    })

    return {
      subBassEnergy,
      bassEnergy,
      midEnergy,
      highEnergy
    }
  }

  // Calculate energy in a frequency band
  calculateBandEnergy(dataArray, startBin, endBin) {
    let sum = 0
    let count = 0

    for (let i = startBin; i < endBin && i < dataArray.length; i++) {
      sum += dataArray[i]
      count++
    }

    // Normalize to 0-1 range
    return count > 0 ? (sum / count) / 255 : 0
  }

  // Real vocal detection using spectral analysis
  async detectVocals(audioBuffer) {
    const offlineContext = new OfflineAudioContext(
      1,
      audioBuffer.length,
      audioBuffer.sampleRate
    )

    const source = offlineContext.createBufferSource()
    source.buffer = audioBuffer

    // Vocals typically sit in 300-3000 Hz range
    const bandpass = offlineContext.createBiquadFilter()
    bandpass.type = 'bandpass'
    bandpass.frequency.value = 1500 // Center of vocal range
    bandpass.Q.value = 1.0

    source.connect(bandpass)
    bandpass.connect(offlineContext.destination)
    source.start(0)

    const filteredBuffer = await offlineContext.startRendering()
    const data = filteredBuffer.getChannelData(0)

    // Calculate energy in vocal range
    let energy = 0
    for (let i = 0; i < data.length; i++) {
      energy += data[i] * data[i]
    }
    energy = Math.sqrt(energy / data.length)

    // Detect harmonic content (vocals have strong harmonics)
    const hasHarmonics = this.detectHarmonics(data, audioBuffer.sampleRate)

    // Combine energy and harmonic detection
    const vocalScore = energy * (hasHarmonics ? 1.5 : 0.5)

    console.log('Vocal detection score:', vocalScore)
    return Math.min(vocalScore, 1.0) // Clamp to 0-1
  }

  // Detect harmonic content (indicates vocals/melodic instruments)
  detectHarmonics(data, sampleRate) {
    // Sample a section of the audio
    const sampleSize = Math.min(4096, data.length)
    const sample = data.slice(0, sampleSize)

    // Perform simple harmonic analysis
    const pitch = this.autocorrelate(sample, sampleRate)

    // If we detect a clear pitch, there are likely harmonics (vocals/melody)
    return pitch > 0
  }

  // Calculate danceability using Essentia spectral features
  calculateDanceability(audioBuffer, frequencies) {
    const essentia = this.initEssentia()

    if (!essentia || !this.essentiaReady) {
      // Fallback: manual calculation
      const danceability = (frequencies.bassEnergy * 1.5 + frequencies.midEnergy * 1.5)
      console.log('Calculated danceability (fallback):', danceability.toFixed(2))
      return Math.min(danceability, 3)
    }

    try {
      const signal = audioBuffer.getChannelData(0)
      const signalVector = essentia.arrayToVector(signal)

      // Use Essentia's spectral features for danceability
      const energy = essentia.Energy(signalVector)

      // Danceability: high energy in bass frequencies
      const danceability = (energy.energy / 1000) * (frequencies.bassEnergy * 2)
      console.log('Essentia danceability:', danceability.toFixed(2))
      return Math.min(danceability, 3)
    } catch (error) {
      console.error('Essentia danceability failed, using fallback:', error)
      const danceability = (frequencies.bassEnergy * 1.5 + frequencies.midEnergy * 1.5)
      return Math.min(danceability, 3)
    }
  }

  // Calculate brightness using Essentia
  calculateBrightness(audioBuffer, frequencies) {
    const essentia = this.initEssentia()

    if (!essentia || !this.essentiaReady) {
      // Fallback: manual calculation
      const totalEnergy = frequencies.subBassEnergy + frequencies.bassEnergy + frequencies.midEnergy + frequencies.highEnergy
      if (totalEnergy === 0) return 0.5
      const brightness = frequencies.highEnergy / totalEnergy
      console.log('Calculated brightness (fallback):', brightness.toFixed(2))
      return brightness
    }

    try {
      const signal = audioBuffer.getChannelData(0)
      const signalVector = essentia.arrayToVector(signal)

      // SpectralCentroidTime: higher = brighter sound (needs sampleRate param)
      const result = essentia.SpectralCentroidTime(signalVector, audioBuffer.sampleRate)
      const brightness = result.centroid / 10000 // Normalize to 0-1 (output property is 'centroid')
      console.log('Essentia brightness:', brightness.toFixed(2))
      return Math.min(brightness, 1)
    } catch (error) {
      console.error('Essentia brightness failed, using fallback:', error)
      const totalEnergy = frequencies.subBassEnergy + frequencies.bassEnergy + frequencies.midEnergy + frequencies.highEnergy
      if (totalEnergy === 0) return 0.5
      return frequencies.highEnergy / totalEnergy
    }
  }

  // Main analysis function
  async analyzeTrack(trackId, streamUrl) {
    // Check cache
    if (this.analysisCache.has(trackId)) {
      console.log('Returning cached analysis for track:', trackId)
      return this.analysisCache.get(trackId)
    }

    console.log('Starting REAL audio analysis for track:', trackId)

    try {
      // Load audio
      const audioBuffer = await this.loadAudio(streamUrl)

      // Perform all analyses in parallel using Essentia.js + Web Audio API
      const [bpm, key, frequencies, vocalScore] = await Promise.all([
        this.detectBPM(audioBuffer),
        this.detectKey(audioBuffer),
        this.analyzeFrequencies(audioBuffer),
        this.detectVocals(audioBuffer)
      ])

      // Calculate danceability and brightness (using Essentia if available)
      const danceability = this.calculateDanceability(audioBuffer, frequencies)
      const brightness = this.calculateBrightness(audioBuffer, frequencies)

      const analysis = {
        trackId,
        bpm,
        key,
        energy: (frequencies.bassEnergy + frequencies.midEnergy + frequencies.highEnergy) / 3,
        vocalScore,
        subBassEnergy: frequencies.subBassEnergy,
        bassEnergy: frequencies.bassEnergy,
        midEnergy: frequencies.midEnergy,
        highEnergy: frequencies.highEnergy,
        danceability, // Calculated from bass+mid energy (0-3 scale)
        brightness, // Calculated from high freq ratio (0-1 scale)
        analyzed: true,
        timestamp: Date.now()
      }

      // Cache the result
      this.analysisCache.set(trackId, analysis)

      console.log('Analysis complete:', analysis)
      return analysis
    } catch (error) {
      console.error('Analysis failed:', error)
      throw error
    }
  }

  // Calculate compatibility between two tracks - VIBE-BASED MATCHING
  // Finds tracks that SOUND SIMILAR, not complementary
  calculateCompatibility(track1Analysis, track2Analysis) {
    let score = 0

    // BPM compatibility (40% weight) - STRICT matching (user requirement: ±3 BPM max)
    const bpmDiff = Math.abs(track1Analysis.bpm - track2Analysis.bpm)
    // User requirement: Only ±3 BPM tolerance for proper blending
    // Anything over 3 BPM = rejected
    if (bpmDiff > 3) {
      // Instant rejection - return 0 score
      return 0
    }
    const bpmScore = Math.max(0, 1 - (bpmDiff / 3)) // Perfect = 1.0, 3 BPM diff = 0
    score += bpmScore * 40 // 40% weight

    // Key compatibility (25% weight) - Camelot wheel
    const keyScore = this.isCompatibleKey(track1Analysis.key, track2Analysis.key) ? 1 : 0.2
    score += keyScore * 25

    // Energy similarity (15% weight) - SIMILAR energy, not complementary
    const energyDiff = Math.abs(track1Analysis.energy - track2Analysis.energy)
    const energyScore = Math.max(0, 1 - (energyDiff / 0.4)) // Within 0.4 = similar vibe
    score += energyScore * 15

    // Danceability similarity (10% weight) - Similar groove
    const danceabilityDiff = Math.abs(track1Analysis.danceability - track2Analysis.danceability)
    const danceabilityScore = Math.max(0, 1 - (danceabilityDiff / 1.0)) // 0-3 scale
    score += danceabilityScore * 10

    // Brightness/Timbre similarity (8% weight) - Similar sonic character
    const brightnessDiff = Math.abs(track1Analysis.brightness - track2Analysis.brightness)
    const brightnessScore = Math.max(0, 1 - (brightnessDiff / 0.3))
    score += brightnessScore * 8

    // Vocal presence similarity (2% weight) - Similar vocal content
    const vocalDiff = Math.abs(track1Analysis.vocalScore - track2Analysis.vocalScore)
    const vocalScore = Math.max(0, 1 - (vocalDiff / 0.5))
    score += vocalScore * 2

    // Total weights: 40 + 25 + 15 + 10 + 8 + 2 = 100%

    console.log('Compatibility breakdown:', {
      bpm: bpmScore * 40,
      key: keyScore * 25,
      energy: energyScore * 15,
      danceability: danceabilityScore * 10,
      brightness: brightnessScore * 8,
      vocal: vocalScore * 2,
      total: Math.round(score)
    })

    return Math.round(score)
  }

  // Check if keys are compatible using Camelot wheel
  isCompatibleKey(key1, key2) {
    // Compatible keys: same key, +1, -1, or same number different letter
    const camelotWheel = {
      '1A': ['1A', '2A', '12A', '1B'],
      '2A': ['2A', '3A', '1A', '2B'],
      '3A': ['3A', '4A', '2A', '3B'],
      '4A': ['4A', '5A', '3A', '4B'],
      '5A': ['5A', '6A', '4A', '5B'],
      '6A': ['6A', '7A', '5A', '6B'],
      '7A': ['7A', '8A', '6A', '7B'],
      '8A': ['8A', '9A', '7A', '8B'],
      '9A': ['9A', '10A', '8A', '9B'],
      '10A': ['10A', '11A', '9A', '10B'],
      '11A': ['11A', '12A', '10A', '11B'],
      '12A': ['12A', '1A', '11A', '12B'],
      '1B': ['1B', '2B', '12B', '1A'],
      '2B': ['2B', '3B', '1B', '2A'],
      '3B': ['3B', '4B', '2B', '3A'],
      '4B': ['4B', '5B', '3B', '4A'],
      '5B': ['5B', '6B', '4B', '5A'],
      '6B': ['6B', '7B', '5B', '6A'],
      '7B': ['7B', '8B', '6B', '7A'],
      '8B': ['8B', '9B', '7B', '8A'],
      '9B': ['9B', '10B', '8B', '9A'],
      '10B': ['10B', '11B', '9B', '10A'],
      '11B': ['11B', '12B', '10B', '11A'],
      '12B': ['12B', '1B', '11B', '12A']
    }

    return camelotWheel[key1]?.includes(key2) || false
  }
}

// Export singleton instance
const audioAnalysisService = new AudioAnalysisService()
export default audioAnalysisService
